import React, { useCallback, useRef, useState } from 'react'
import { Upload, FileText, Trash2 } from 'lucide-react'
import { Button } from './button'

export type FileDropzoneProps = {
  accept?: string[]
  maxSize?: number
  onDrop: (accepted: File[]) => void
  multiple?: boolean
  placeholder?: string
  maxSizeLabel?: string
}

export const FileDropzone: React.FC<FileDropzoneProps> = ({ 
  accept, 
  maxSize, 
  onDrop, 
  multiple = true,
  placeholder = 'Click to upload or drag and drop',
  maxSizeLabel
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [dragActive, setDragActive] = useState(false)

  const openFileDialog = useCallback(() => {
    inputRef.current?.click()
  }, [])

  const handleFiles = useCallback((fileList: FileList | null) => {
    if (!fileList) return
    const files = Array.from(fileList)
    const filtered = files.filter((f) => {
      const typeOk = !accept || accept.length === 0 || !f.type || accept.includes(f.type)
      const sizeOk = !maxSize || f.size <= maxSize
      return typeOk && sizeOk
    })
    onDrop(filtered)
  }, [accept, maxSize, onDrop])

  const onDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault()
    setDragActive(true)
  }
  const onDragLeave: React.DragEventHandler<HTMLDivElement> = () => setDragActive(false)
  const onDropHandler: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault()
    setDragActive(false)
    handleFiles(e.dataTransfer?.files || null)
  }

  return (
    <div>
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDropHandler}
        onClick={openFileDialog}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed p-6 text-center transition ${dragActive ? 'border-brand-600 bg-brand-50/60' : 'border-black/10 bg-black/5 hover:bg-black/10'}`}
      >
        <Upload className="h-6 w-6 text-foreground/60" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">{placeholder}</p>
          {maxSizeLabel && <p className="text-xs text-foreground/60">{maxSizeLabel}</p>}
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          accept={accept && accept.length > 0 ? accept.join(',') : undefined}
        />
      </div>
    </div>
  )
}

// Component for displaying uploaded file(s) with remove option
export type FileListItemProps = {
  file: File
  onRemove?: () => void
}

export const FileListItem: React.FC<FileListItemProps> = ({ file, onRemove }) => {
  return (
    <div className="flex items-center justify-between rounded-lg border border-black/10 px-3 py-2">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <FileText className="h-4 w-4 text-foreground/70 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-foreground truncate">{file.name}</div>
          <div className="text-xs text-foreground/60">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
        </div>
      </div>
      {onRemove && (
        <Button type="button" onClick={onRemove} variant="ghost" size="sm">
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}

export default FileDropzone
